/* The readout chart's own chrome. bklit draws the series, the grid, the crosshair and the mark;
 * everything with a colour or a word in it is here.
 *
 * ── One rule in this file is load-bearing rather than cosmetic ────────────────────────────────
 * `.surface` is `pointer-events: none`. It is an HTML layer laid OVER the plot's `<svg>`, and the
 * pointer handlers that resolve the nearest point live on a `<g>` inside that svg. Without the
 * property the overlay eats every mousemove and the whole hover readout is dead — silently, since
 * the chart still draws. Keyboard focus is unaffected by `pointer-events`, which is what lets the
 * same element be the tab stop and the accessible name while the pointer passes straight through.
 */

/* `position: relative` is NOT decoration: the visually-hidden data table beside the plot is
 * `position: absolute`, and an absolute box is laid out against its nearest POSITIONED ancestor.
 * Without one here it reached whichever ancestor happened to be positioned — on the Ankiety tab
 * that was the page's `<main>`, three boxes outside the chart strip's own scroller — so the hidden
 * tables of the cards scrolled off to the right were placed at their static x in the PAGE, escaped
 * the scroller's clip, and dragged the document's `scrollWidth` 382px past the viewport. A whole-
 * page horizontal scrollbar from something nobody can see, on a surface whose every box fits. */
.figure {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
}

/* The chart's box. `position: relative` is what every portalled layer below positions against —
 * bklit portals its tooltip here too. */
.plot {
  position: relative;
  inline-size: 100%;
}

/* The axis labels' layer: a full-bleed box the labels position inside, so a label's coordinates
 * are the chart's own pixel coordinates and nothing has to be converted. */
.axes {
  position: absolute;
  inset: var(--space-0);
  pointer-events: none;
}

/* The canon's `tickSt` on every plot in `Detal klienta v3`: 9.5 / 600 / `--text-3`. The ink stood
 * at `--text-2` because the tertiary measures 2.17–2.80 on a sunken surface; the owner withdrew
 * the measured departures on 1 September 2026 and the numbers are in that round's report. */
.yTick,
.xTick {
  position: absolute;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* Right-aligned against the plot's left edge, and centred on the grid line it names. */
.yTick {
  transform: translateY(-50%);
}

/* The 18.09 Pomiary canon draws its gutter at 11px / 400 / `--chart-label`, which is `--text-2` —
 * a whole-card plot can carry a legible axis where a 150px card cannot. Worn only by a chart that
 * passes a `plot` spec, so every other chart in this app keeps the 9.5 / 600 above to the pixel. */
.tickCanon {
  font-size: var(--fs-caption);
  font-weight: var(--fw-regular);
  color: var(--chart-label);
}

.surface {
  position: absolute;
  pointer-events: none;
}

/* The plot is a tab stop, so it needs the app's ring rather than the browser's blue one. Drawn
 * OUTSIDE the box: the ring sits in the margin the axis labels live in, where it cannot cover the
 * first and last marks. */
.surface:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* ── the floating readout panel ───────────────────────────────────────────────────────────────
 * bklit's `TooltipBox` supplies the position, the flip at the right edge and the spring; the panel
 * inside it is ours. No glass and no coloured shadow — a KANON card with a hairline, which is what
 * `--chart-tooltip-background` already resolves to. */
.tooltip {
  z-index: 2;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-7px) var(--space-2);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
  background: var(--bg-card);
  box-shadow: var(--shadow-card);
}

.panelDate {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
}

.panelValue {
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-heading);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The 18.09 Pomiary canon's own `.tip`: a wider box on the sunken surface, holding the date, the
 * figure at 15px, and the two lines a full-card plot has room to say — what changed since the
 * previous reading, and who recorded it. Worn only by a chart that passes a `plot` spec. */
.panelWide {
  min-width: var(--w-readout-panel);
  padding: var(--space-2) var(--space-10px);
  background: var(--bg-inset);
  border-radius: var(--radius-sm);
}

.panelWide .panelDate {
  font-size: var(--fs-micro);
}

.panelWide .panelValue {
  font-size: var(--fs-readout-figure);
  font-weight: var(--fw-bold);
}

/* The change since the previous reading, and the source. Quieter than the figure, and the same ink
 * every caption on this app wears. */
.panelNote {
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* A week nobody reported. Said in words, never drawn as a zero — the same rule the line follows. */
.panelGap {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  font-style: normal;
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
}

/* A `span`'s mean, named in the right gutter at the height of its line (`ReadoutPlotSpec.span`):
 * the figure the verdict reads, in the page's ink and the label weight, centred on the line. */
.spanLabel {
  position: absolute;
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transform: translateY(-50%);
}
